import { emailCompositionDefaults } from '@kula-ai/components';
import {
  isSameEmailValue,
  type EmailUser,
} from '@/components/shared/email/emailValue';
import type { EmailCompositionValue } from '@kula-ai/components';

export type RejectFromUser = EmailUser;

export interface RejectEmailConfig {
  from?: string;
  fromUserObject?: RejectFromUser;
  subject?: string;
  body?: string;
  cc?: string[];
  bcc?: string[];
  survey?: { value?: string | number; label?: string } | null;
  /**
   * The picked template's id, as the option value (a string).
   *
   * The composer used to hold this in local state alone, so it never survived
   * a remount — toggling "Send email to candidate" unmounts the composer, and
   * the edit flow never had it to begin with. Both reopened on a blank picker
   * while the body was already a template's (ENG-3828).
   */
  template?: string | null;
}

/** Seeds the composer from the reject form's stored shape. */
export const toEmailValue = (
  config: RejectEmailConfig | undefined | null,
  candidateEmail?: string
): EmailCompositionValue => ({
  ...emailCompositionDefaults,
  // Adopted by the composer once confirmed selectable — see RejectEmailComposer.
  from: '',
  to: candidateEmail ?? '',
  cc: config?.cc ?? [],
  bcc: config?.bcc ?? [],
  subject: config?.subject ?? '',
  content: config?.body ?? '',
  template: config?.template ?? null,
});

export type RejectAttachment = File | Record<string, unknown>;

/** Stable key so the editor's remove button maps a chip back to its entry. */
export const attachmentKey = (item: RejectAttachment, index: number): string =>
  item instanceof File
    ? `file:${item.name}:${item.size}:${item.lastModified}`
    : `id:${(item as { id?: unknown })?.id ?? index}`;

/** Maps Formik's mixed attachment array to kula `Editor` chips. */
export const toEditorAttachments = (items: RejectAttachment[] = []) =>
  items.map((item, index) => {
    const key = attachmentKey(item, index);
    if (item instanceof File) {
      return { key, name: item.name, size: item.size, mimeType: item.type };
    }
    const row = item as Record<string, unknown>;
    return {
      key,
      name: String(row.filename ?? row.name ?? 'file'),
      size: typeof row.byte_size === 'number' ? row.byte_size : undefined,
      mimeType:
        typeof row.content_type === 'string' ? row.content_type : undefined,
      url: typeof row.url === 'string' ? row.url : undefined,
    };
  });

/** Guards against `Email`'s re-emits being stored as new state, which loops. */
export { isSameEmailValue };

/**
 * Maps a snapshot back to the shape `getRejectionEmailFormData` reads.
 *
 * `from` stays the sender's email (the send path matches on it); shared
 * mailboxes read `fromUserObject`, so one selection emits both.
 */
export const fromEmailValue = (
  value: EmailCompositionValue,
  resolveFromUser: (_from: string) => RejectFromUser | undefined
): RejectEmailConfig => {
  const fromUserObject = resolveFromUser(value.from);

  return {
    from: fromUserObject?.email ?? value.from,
    ...(fromUserObject ? { fromUserObject } : {}),
    subject: value.subject,
    body: value.content,
    cc: value.cc ?? [],
    bcc: value.bcc ?? [],
    template: value.template ?? null,
  };
};
